import AuthService from '../services/authService';

const STORAGE_KEY = 'selectedCompany';

/** UTF-8–safe base64 (btoa alone throws on Turkish chars in names). */
const toBase64 = (str) => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const fromBase64 = (b64) => {
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/** Read selected company from localStorage (shared key with tahsis). */
export const getStoredSelectedCompany = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Resolve tahsis SPA base URL (local vs prod via config.js). */
export const getTahsisAppUrl = () => {
  const configured = window.APP_CONFIG?.tahsisAppUrl;
  if (configured) return String(configured).replace(/\/+$/, '');
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return isLocal ? 'http://localhost:5173' : 'https://tahsis.fidost.com';
};

/** Build base64 SSO hash payload for cross-app navigation. */
export const buildSsoHash = (selectedCompany = null, extra = {}) => {
  const token = AuthService.getAuthToken();
  const refreshToken = AuthService.getRefreshToken();
  const userData = AuthService.getCurrentUser();
  if (!token || !userData) return null;

  const rememberMe = !!localStorage.getItem('authToken');
  const company = selectedCompany ?? getStoredSelectedCompany();
  const payload = { token, refreshToken, userData, rememberMe, ...extra };
  if (company?.id) payload.selectedCompany = company;

  try {
    return `#sso=${toBase64(JSON.stringify(payload))}`;
  } catch (err) {
    console.error('[sso] Failed to build SSO hash', err);
    return null;
  }
};

/**
 * Apply inbound SSO from URL hash. Returns true if a payload was processed.
 * Call once on app boot before auth-gated UI renders.
 */
export const applySsoFromHash = () => {
  const hash = window.location.hash;
  if (!hash.startsWith('#sso=')) return false;

  try {
    const raw = fromBase64(hash.slice(5));
    const { token, refreshToken, userData, rememberMe, selectedCompany } = JSON.parse(raw);
    if (token && userData) {
      AuthService.setAuthData(token, userData, rememberMe || false, refreshToken);
      if (selectedCompany?.id) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedCompany));
        localStorage.setItem('selectedCompanyId', String(selectedCompany.id));
      }
      window.dispatchEvent(new Event('userLogin'));
    }
  } catch {
    // Malformed SSO payload
  }

  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return true;
};

/**
 * Same-tab redirect to tahsis (login / company home).
 * @param {{ changeCompany?: boolean, withSso?: boolean, auth?: 'login'|'register'|'forgot'|null }} options
 */
export const redirectToTahsis = (options = {}) => {
  const { changeCompany = false, withSso = true, auth = null } = options;
  const base = getTahsisAppUrl();
  const authQuery =
    auth && auth !== 'login' ? `?auth=${encodeURIComponent(auth)}` : '';

  if (withSso && AuthService.isAuthenticated()) {
    const hash = buildSsoHash(null, changeCompany ? { changeCompany: true } : {});
    window.location.assign(`${base}/${authQuery}${hash || ''}`);
    return;
  }
  window.location.assign(`${base}/${authQuery}`);
};
